import type { Driver } from '#domain/model/Driver.ts';
import { apiDriverAdministration } from '#store/apiDriverAdministration.ts';

import { zDriversEmbeddedList } from './model/generated/zod.gen.ts';
import type { ApiDriverEmbedded, ApiDriversEmbeddedList } from './model/types.ts';

const DRIVER_STATUS = 'active';
export const DRIVER_ADMINISTRATION_API_LIMIT = 500;

const mapDriver = (driver: ApiDriverEmbedded): Driver => ({
    id: driver.id,
    name: driver.display_name ?? ([driver.first_name, driver.last_name].filter(Boolean).join(' ') || driver.id),
});

type DriversQueryParams = {
    limit: number;
};

type DriversAfterQueryParams = DriversQueryParams & {
    after: string;
};

export type GetDriversResponse = {
    drivers: Driver[];
    after: string | undefined;
};

const getAfterFromNext = (nextHref: ApiDriversEmbeddedList['_links']['next'] | undefined) => {
    const next = nextHref?.href;
    if (!next) {
        return;
    }

    try {
        const parsedUrl = new URL(next);
        return parsedUrl.searchParams.get('after') ?? undefined;
    } catch {
        const queryStartIndex = next.indexOf('?');
        if (queryStartIndex >= 0) {
            const queryParams = new URLSearchParams(next.slice(queryStartIndex + 1));
            return queryParams.get('after') ?? undefined;
        }
    }

    return;
};

const transformDriversResponse = (response: unknown): GetDriversResponse => {
    const parsed = zDriversEmbeddedList.parse(response);
    return {
        drivers: (parsed.items ?? []).map(mapDriver),
        after: getAfterFromNext(parsed._links.next),
    };
};

export const driverAdministrationSlice = apiDriverAdministration.injectEndpoints({
    endpoints: build => ({
        getDrivers: build.query<GetDriversResponse, DriversQueryParams>({
            query: params => ({
                url: '/drivers',
                params: {
                    ...params,
                    status: DRIVER_STATUS,
                },
            }),
            transformResponse: transformDriversResponse,
        }),
        getDriversAfter: build.query<GetDriversResponse, DriversAfterQueryParams>({
            query: params => ({
                url: '/drivers',
                params: {
                    ...params,
                    status: DRIVER_STATUS,
                },
            }),
            transformResponse: (response, _meta, arg) => {
                const parsedResponse = transformDriversResponse(response);
                const afterChanged = parsedResponse.after !== arg.after;

                return {
                    ...parsedResponse,
                    after: afterChanged ? parsedResponse.after : undefined,
                };
            },
        }),
    }),
});

export const { useGetDriversAfterQuery, useGetDriversQuery } = driverAdministrationSlice;
